import * as React from 'react';
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import {
  eachDayOfInterval,
  endOfYear,
  format,
  getWeek, isEqual,
  isToday,
  isWeekend,
  startOfWeek, startOfYear, endOfWeek, getISOWeek
} from 'date-fns';
import { groupBy } from 'fp-ts/NonEmptyArray';
import './Planner.scss';
import { observer } from 'mobx-react';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Picker,
  Spinner,
  Tooltip,
  GroupedSelect,
  AutoComplete,
  TextHighlighter,
  useDebounce,
  Layout, SegmentedItem, EmptyState,
  notification, NotificationType
} from '@iba-team/portal-react-components/dist';
import { ReactComponent as SaveIcon } from '@iba-team/portal-react-components/dist/svg/outline/document.svg';
import { ReactComponent as EmptyList } from '../../assets/icons/no-requests.svg';
import { ReactComponent as RemoveIcon } from '@iba-team/portal-react-components/dist/svg/outline/trash.svg';
import { PlannerStore } from '../../store/Planner';
import { NewListModal, NewListModalI } from './modals/NewListModal';
import { DeleteListModal, DeleteListModalI } from './modals/DeleteListModal';
import { AddEmployeeModal, PlannerModals } from './modals/AddEmployeeModal';
import {
  EmployeeEvent,
  EmployeesEvents,
  EmployeesPlanerI,
  PlannerListDTO,
  PlannerListItem,
  PlannerTypeUnion
} from '../../types';
import { useSetState } from 'react-use';
import { toHumanCase, tail } from '../../utils/functions';
import ReactDOM from 'react-dom';
import { buildModal } from '../../containers/MainWrapper/modal/ModalBuilder';
import { EmployeeEventWithNewState, ItemRow } from './ItemRow';
import { TypeLegend } from '../../utils_components/TypeLegend';
import { ArrowButtons } from '../../utils_components/ArrowButtons';
import { PLANNER_ROUTE } from '../../utils/consts';
import { useLocation } from 'react-router-dom';
import UserLink from '../../utils_components/UserLink';
import NewListLink from '../../utils_components/NewListLink';

interface PlannerModalsI extends  PlannerModals{
  deleteSavedList?: () => void
}


const appEmployeeModalCall =
  buildModal<undefined, PlannerModalsI>((currentItem, close)=> <AddEmployeeModal   {...currentItem} onClose={close}/> );
const deleteListModalCall =
  buildModal<undefined, DeleteListModalI >((currentItem, close) => <DeleteListModal {...currentItem} onClose={close} />);
const newListModalCall =
  buildModal<undefined, NewListModalI>((currentItem, close) => <NewListModal {...currentItem} onClose={close}/>);

export type DateIndexes = 1 | 2 | 3 | 4 | 5 | 6 | 7 | undefined

export const fetchTooltipDesc = (item: EmployeeEvent | undefined,t:  TFunction<'Translation'>, withoutStatus? : false) => {
  if (!item) {
    return '';
  }

  let desc = '';
  if (item.countryCode && item.type === 'HOLIDAY') {
    desc = t(item.countryCode) + ': ' + t('nonWorkingDay');
  } else if (item.countryCode && item.type === 'WORKING') {
    desc = t(item.countryCode) + ': ' + t('workingDay');
  } else {
    desc = (withoutStatus === undefined) ? (toHumanCase(item.originalTypeName || '') + ': ' + toHumanCase(item.status || '')) : toHumanCase(item.originalTypeName || '');
  }
  return desc;
};

const groupByWeek = (list: Date[], currentDate: Date) => {
  return groupBy((date: Date) => getWeek(date, {
    weekStartsOn: 1,
    firstWeekContainsDate: endOfWeek(startOfYear(currentDate), { weekStartsOn: 1 }).getDate() as unknown as DateIndexes
  }) as unknown as string)(list);
};

export const monthToString = (date: Date) => {
  return format(date, 'MMMM');
};


export const weekdayStylesExpression = (item: Date, isHeaderCell: boolean) => {
  const today = isToday(item);
  const weekend = isWeekend(item);

  if (!isHeaderCell) {
    if (weekend && !today) {
      return 'planner_table_cell planner_table_cell__bg-gray';
    }
    if ((weekend && today) || today) {
      return 'planner_table_cell planner_table_cell__bg-blue';
    }
    return 'planner_table_cell';
  }

  if (today && weekend) {
    return 'planner_table_cell__bold planner_table_cell__bg-gray';
  }
  if (weekend) {
    return 'planner_table_cell__bg-gray';
  }
  if (today) {
    return 'planner_table_cell__bold';
  }
};
export const handleTdClassName = (item: EmployeeEvent | undefined, elem: Date, isHeaderCell: boolean) => {
  if (!item) {
    return weekdayStylesExpression(elem, isHeaderCell);
  }

  let classColor;
  if (item.status === 'PENDING') {
    classColor = 'orange';
  } else if (item.status === 'APPROVED') {
    switch (item.type) {
    case ('VACATION'):
      classColor = 'green';
      break;
    case ('SICK_LEAVE'):
      classColor = 'red';
      break;
    default:
      classColor = 'gray';
    }
  } else if (isToday(elem)) {
    classColor = 'blue';
  } else {
    classColor = (item.type === 'HOLIDAY') ? 'gray' : 'white';
  }
  return 'planner_table_cell planner_table_cell__bg-' + classColor;
};

export const handleEventType = (event: EmployeeEventWithNewState) => {
  return (event?.new) ? event.new: event?.default;
};
export const SpinnerPortal: FunctionComponent<{isRender: boolean, className?: string}> = (props) => {
  return  ReactDOM.createPortal(
    <div className={`${props.className} planner_page_spinner`} style={{display: props.isRender ? 'flex' : 'none' }}>
      <Spinner className={'spinner'}/>
    </div>, document.body
  );
};
export const initialList = { id: 0, name: '', ownerId: 0, userIds: [], plannerListType: {
  editable: true,
  type: 'CUSTOM' as unknown as PlannerTypeUnion
} };

export const Planner: FunctionComponent = observer(() => {
  const { getAllLeaveTypes, allLeaveTypes } = LeaveTypesStore;
  const { getEmployeesEvents,
    employeesEvents,
    listEmployees,
    plannerLists,
    fetchPlannerLists,
    removeEmployee,
    clearEmployeesEvents,
    fetchingEvents,
    getPlannerFilters,
    addPlannerFilter,
    getListEmployees,
    selectListFilter,
    clearListEmployees,
    updatePlannerList
  } = PlannerStore;
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentList, setCurrentList] = useSetState<PlannerListDTO>(initialList);
  const startOfCurrentWeek = useRef<null | HTMLTableDataCellElement>(null);
  const isPreviewList = currentList.id === 0 && !currentList.name.length;
  const searchPlannerRef = useRef<HTMLInputElement>(null);
  const [newEmployee, setNewEmployee] = useSetState<{employee?: EmployeesPlanerI, typingValue: string}>({
    employee: undefined,
    typingValue: ''
  });

  const searchEmployeeRequest = useDebounce(getListEmployees, 300);

  const patchCurrentList = async (userId: number) => {
    try {
      updatePlannerList(currentList.id, userId)
        .then(res=> {
          setCurrentList?.({userIds: tail([res.userIds]) as unknown as number[]});
        })
        .then(() => {
          fetchPlannerLists();
        });
      notification.open({
        type: NotificationType.SUCCESS,
        title:  t('success'),
        description: t('employeeAddToList')
      });
    } catch (error) {
      notification.open({
        type: NotificationType.ERROR,
        title:  t('error'),
        description: t('error')
      });
    }
  };

  useEffect(() => {
    if(newEmployee.employee){
      if(!isPreviewList) {
        if (currentList.plannerListType.editable) {
          patchCurrentList(newEmployee.employee.id);
        } else {
          appEmployeeModalCall.showImmediately({setCurrentList: setCurrentList, currentId: newEmployee.employee.id });
        }
      }
      else{
        setCurrentList({userIds: [...currentList.userIds, newEmployee.employee.id]});
      }
    }
  },[newEmployee.employee]);

  useEffect(() => {
    if(newEmployee.typingValue.length > 2){
      searchEmployeeRequest(newEmployee.typingValue, currentList?.userIds || []);
    }
    else{
      clearListEmployees();
    }
  },[newEmployee.typingValue]);

  useDeepCompareEffect(() => {
    const currList = selectListFilter.find(i => i.element === 'ID');
    const selected = Object.values(plannerLists).flat().find(i => i.id.toString()  === currList?.value?.toString());

    if(selected){
      setCurrentList(selected);
    } else {
      setCurrentList(initialList);
    }
  },[selectListFilter, plannerLists]);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const doShowNewListModalRef = useRef(queryParams.get('doShowNewListModal'));

  useEffect(() => {
    // for "Who's off today" -> "New List" link
    doShowNewListModalRef.current && showNewListModal(true, true);
  },[]);

  useEffect(() => {
    getPlannerFilters();
  },[]);

  useEffect(() => {
    handleScrollPosition();
  }, [currentList, employeesEvents, plannerLists]);

  useEffect(()=>{
    getAllLeaveTypes();
  },[]);

  useDeepCompareEffect(() => {
    if (currentList.userIds.length) {
      getEmployeesEvents(currentDate.getFullYear(), currentList.userIds, currentList.plannerListType.type, currentList.name);
    } else {
      clearEmployeesEvents();
    }
    setNewEmployee({typingValue: '', employee: undefined});
  }, [currentList, currentDate]);

  useEffect(() => {
    fetchPlannerLists(false, !!doShowNewListModalRef.current);

    // once Planner + New list modal are displayed, environment cleanup must
    // be done to prevent unnecessary updates in the widget_analytics table. 
    doShowNewListModalRef.current = null;
  }, [selectListFilter]);

  const dateList: Date[] = useMemo(() => {
    return eachDayOfInterval({
      start: startOfWeek(startOfYear(currentDate), { weekStartsOn: 1 }),
      end: endOfWeek(endOfYear(currentDate), { weekStartsOn: 1 })
    });
  },[currentDate]);

  const grouped = useMemo(() => {
    return Object.values(groupByWeek(dateList, currentDate));
  },[dateList, currentDate]);


  const unPackageGroupedList = useMemo(() => {
    return grouped.flat();
  },[grouped]);

  const weekFirstDay = startOfWeek(currentDate, { weekStartsOn: 1 });
  const datesMapping = (list: Date[], variant: string) => {
    return list.map(cell => (
      <td
        ref={isEqual(weekFirstDay, new Date(cell)) ? startOfCurrentWeek : null}
        key={cell.toString()}
        className={weekdayStylesExpression(new Date(cell), true)}
      >{format(new Date(cell), variant)}
      </td>
    ));
  };

  const stringDays = datesMapping(unPackageGroupedList, 'EEEEEE');

  const numberDays = datesMapping(unPackageGroupedList, 'd');

  const prevYear = () => {
    const today = new Date();
    if (today.getFullYear() !== currentDate.getFullYear() - 1) {
      setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1));
    } else {
      setCurrentDate(today);
    }
  };

  const nextYear = () => {
    const today = new Date();
    if (today.getFullYear() !== currentDate.getFullYear() + 1) {
      setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
    } else {
      setCurrentDate(today);
    }
  };

  const removeEmployees = async (listId: number, userIds: number[]) => {
    await removeEmployee(listId, userIds);
    await fetchPlannerLists();
  };

  const renderListTitle =  () => {
    return <div className={'planner_table__employees'}>
      <> { currentList.userIds.length === 1 ? t('singleEmployee')
        : t('employeesCount', {count: currentList.userIds.length})}
      </>

    </div>;
  };

  const handleRemoveEmployee = (employees: EmployeesEvents) => {
    if(!isPreviewList){
      removeEmployees(currentList.id, [employees.id])
        .then(()=> {
          setCurrentList({...currentList, userIds: currentList.userIds.filter(i=> i !== employees.id) });
        });
    }
    else{
      setCurrentList({...currentList, userIds: currentList.userIds.filter(i=> i !== employees.id) });
      getEmployeesEvents(currentDate.getFullYear(), currentList.userIds.filter(i=> i !== employees.id), currentList.plannerListType.type, currentList.name );
    }
  };

  const tableRef = useRef<HTMLTableElement>(null);
  const employeeRef = useRef<HTMLDivElement>(null);

  const onScrollTable = (e:any ) => {
    if(tableRef?.current && employeeRef.current){
      employeeRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };
  if(tableRef?.current && employeeRef.current){
    tableRef?.current.addEventListener('scroll', onScrollTable);
  } else {
    window.removeEventListener('scroll', onScrollTable);
  }

  const onScrollEmployee = (e:any) =>{
    if(tableRef?.current && employeeRef.current){
      tableRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };
  if(tableRef?.current && employeeRef.current){
    employeeRef?.current.addEventListener('scroll', onScrollEmployee);
  } else {
    window.removeEventListener('scroll', onScrollEmployee);
  }
  const isLoadSpinner = fetchingEvents;

  const handleScrollPosition = () => {
    if(startOfCurrentWeek.current) {
      startOfCurrentWeek.current.scrollIntoView({
        inline: 'start',
        block: 'end',
      });
    }
  };

  const onToday = useCallback(() => {
    setCurrentDate(new Date());
    handleScrollPosition();
  }, [currentList, employeesEvents, plannerLists]);

  const focusOnSearchEmployees = () => {
    searchPlannerRef.current?.focus();
  };

  const clearPlannerList = () => {
    setCurrentList(initialList);
    clearEmployeesEvents();
    focusOnSearchEmployees();
  };

  const showNewListModal = (isListEmpty: boolean, isFromWhoisOffToday?: boolean) => {
    newListModalCall.showImmediately({
      doCreateEmptyList: isListEmpty,
      currentList: currentList,
      addPlannerFilter: addPlannerFilter,
      getPlannerFilters: getPlannerFilters,
      onGoWithoutSaving: clearPlannerList,
      onSubmit: focusOnSearchEmployees,
      isForWhoisOffToday: !!isFromWhoisOffToday
    });
  };

  const onChangeList = async(value: PlannerListItem) => {
    await addPlannerFilter([
      {
        element: 'SELECTED_LIST_NAME',
        value: value.name
      },
      {
        element: 'LIST_TYPE',
        value: value.plannerListType.type
      },
      {
        element: 'ID',
        value: value.id
      },
    ], true);
    await getPlannerFilters();
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const listToArray: [string, PlannerListItem[]][] = [[t('CIRCLE'), plannerLists.CIRCLE], [t('PROJECT'), plannerLists.PROJECT], [t('CUSTOM'), plannerLists.CUSTOM],  [t('DIVISION'), plannerLists.DIVISION]].filter(i => i[1]);
  const isEmptyList = !listToArray.length;

  const renderLayoutHeader = () => {
    return {
      layoutName: <div style={{display: 'flex'}}>
        <h2 className={'planner_header'}>{ t('planner') }</h2>
        <div className={'planner_top'} style={{marginLeft: '8px'}}>
          <div className={'planner_top__select'}>
            <GroupedSelect
              customDropdownWidth={'320px'}
              onChangeValue={onChangeList}
              value={currentList.name.length ? currentList.name : <p className={'empty'}>{t('chooseList')}</p> as unknown as string}
              onHeaderClick={() => showNewListModal(true)}
              list={listToArray}
              render={(el) => <div title={el.name} className={'plannerListItemOuter'}>
                {el.name}
              </div> }
              renderHeaderContent={<div onClick={() => setCurrentList(initialList)}> {t('newList')}</div>}
              disabled={isEmptyList}
              uniqueElement={(el) => (el.id === currentList.id) }
            />
          </div>

          <NewListLink
            to={`${PLANNER_ROUTE}`}
            openInNewTab={false}
            onClick={ (e) => {
              e.preventDefault();
              showNewListModal(true);
            }}
          />
        </div>
      </div>,
      right:  <div className={'planner_header__inner'}>
        <TypeLegend list={allLeaveTypes} renderItem={i => i.requestName}/>
        <SegmentedItem onClick={() => onToday()}>
          Today
        </SegmentedItem>
        <ArrowButtons onLeftClick={prevYear} onRightClick={nextYear}/>
        <Picker picker={'year'}
          onChange={e => setCurrentDate(new Date(e._d))}
          format={'YYYY'}
          value={currentDate.getFullYear().toString()}
          className={'customPickerReportSector'}/>
      </div>
    };
  };
  return (
    <>
      <SpinnerPortal isRender={isLoadSpinner}/>
      <Layout header={renderLayoutHeader()} className={'planner planner_page_layout'}>
        <div className={'planner_table__wrapper'} >
          <div className='planner_table__wrapper-search-block'>
            <div className={'filterWrapper'}>
              <div className={'planner_table__list-input'}>
                {renderListTitle()}
                <div className={'planner_buttons'}>
                  { ((employeesEvents.length >= 1) && !currentList.name && (currentList.userIds.length > 0))  &&
                        <div
                          className={'planner_table__save-list'}
                          onClick={() => {
                            showNewListModal(false);
                          }}
                        >
                          <SaveIcon />{t('saveList')}
                        </div>
                  }
                  {
                    (!currentList.name && (employeesEvents.length >= 1) && (currentList.userIds.length > 0)) &&
                        <div className='planner_table__delete-list' onClick={() => {
                          setCurrentList({ userIds: []});
                          clearEmployeesEvents();
                        }}><RemoveIcon/>{ t('removeAll') }</div>
                  }
                  { (currentList.name && currentList.plannerListType.editable) &&
                        <div className='planner_table__delete-list' onClick={()=> {
                          deleteListModalCall.showImmediately({
                            currentList: currentList,
                            setCurrentList: setCurrentList
                          });
                        }}><RemoveIcon/>{ t('deleteList') }</div>
                  }
                </div>
              </div>
              <div className='planner_table__wrapper-search-block_margin-bottom-search'>
                <AutoComplete ref={searchPlannerRef} className={'searchSelect'} placeholder={t('typeToSearch')} list={listEmployees}
                  uniqueElement={i => i.id === newEmployee.employee?.id}
                  renderDropdownItem={(person) =>  <>
                    <TextHighlighter data={person.name} compareString={newEmployee.typingValue}/>
                    <div className='position_person'>
                      {person.positionName}
                    </div>
                  </> }
                  onChange={(value) => setNewEmployee({typingValue: value})}
                  onSelect={empl => setNewEmployee({employee: empl})}
                  notFoundContent={{title: 'Employee not found or is already in the list', description: 'Please try again'}}
                  onClear={() => setNewEmployee({employee: undefined})} />
              </div>
            </div>
            <div className='employee-block' ref={employeeRef}>
              {currentList.userIds.length ? employeesEvents.map((employee) => {
                return (
                  <div className={'employee'} key={employee.id}>
                    <UserLink
                      id={employee.id}
                      fullName={employee.fullName}
                      countryCode={employee.countryCode}
                      userActive={employee.isUserActive}
                      profileLinkVisible={employee.isProfileLinkVisible}
                      openInNewTab={true}>
                    </UserLink>                    
                    {currentList.plannerListType.editable &&
                        <Tooltip title={t('remove')}>
                          <RemoveIcon onClick={() => handleRemoveEmployee(employee)} />
                        </Tooltip>
                    }
                  </div>
                );
              })
                :
                <div className='employee-block-default'>
                  <EmptyState className={'empty'} title={t('emptyListTitle')}
                    description={t('emptyListDescr')}
                    icon={<EmptyList className={'emptyProject'} />}/>

                  {
                    unPackageGroupedList.map((elem, index) => {
                      return <td key={index} className={'emptyRow'} rowSpan={4}></td>;
                    })
                  }
                </div>
              }
            </div>
          </div>
          <table className={'planner_table'} ref={tableRef}>
            <div className='planner_table__thead-stiky'>
              <thead>
                <div>
                  <tr>
                    {grouped.map((dateSet, idx) => {
                      const weekIndex = getISOWeek(dateSet[0]);
                      const firstM = monthToString(dateSet[0]);
                      const lastM = monthToString(dateSet[dateSet.length-1] ?? dateSet[0]);
                      const isTwoMonthGroup = (firstM !== lastM);
                      return (
                        <td
                          key={idx} colSpan={7} className={'bgCustom'}
                        >Week {weekIndex}: {isTwoMonthGroup ? `${firstM}-${lastM}` : `${firstM}`}
                        </td>
                      );
                    })
                    }
                  </tr>
                  <tr className={'planner_days'}>
                    {stringDays}
                  </tr>
                  <tr className={'planner_dates'}>
                    {numberDays}
                  </tr>
                </div>
              </thead>
            </div>
            <tbody>
              <div>
                { currentList.userIds.length > 0 && !fetchingEvents &&
                  <>
                    { employeesEvents.map((employee)=>
                    {
                      return <ItemRow employeeData={employee} unPackageGroupedList={unPackageGroupedList}/>;
                    })
                    }
                  </>
                }
              </div>
            </tbody>
          </table>
        </div>
      </Layout>
    </>
  );
});


